import { Component, HostListener, Input } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DeviceTypeService } from '@services/device-type.service';
import { ConnectorFilter, FileType, SearchService, TypeCode } from '@services/search.service';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-search-results-filters',
  templateUrl: './search-results-filters.component.html',
  styleUrls: ['./search-results-filters.component.scss'],
  standalone: false,
})
export class SearchResultsFiltersComponent {
  public readonly maxDisplayed = 10;
  public sourceFilter = '';
  public searchQuery = '';
  public displayedConnectors = this.maxDisplayed;
  public displayedResultType = this.maxDisplayed;
  private filterChanges: { filterType: string; filter: string }[] = [];
  private newDateSave = '';
  private saveSubscription: Subscription | null = null;
  @Input() innerContent = false;
  @Input() autoSave = true;
  @Input() discardFiltersSaveEvent: Observable<void> | null = null;

  constructor(
    public router: Router,
    public searchService: SearchService,
    public translateService: TranslateService,
    public deviceType: DeviceTypeService,
  ) {}

  ngOnInit() {
    const startDate = (' ' + this.searchService.selectedDateRange().start).slice(1);
    const endDate = (' ' + this.searchService.selectedDateRange().end).slice(1);
    const lastSelectedRange = (' ' + this.searchService.selectedPresetDate()).slice(1);
    if (this.discardFiltersSaveEvent != null)
      this.saveSubscription = this.discardFiltersSaveEvent.subscribe(() => {
        this.searchService.selectedPresetDate.set(this.newDateSave);
        this.searchService.selectedDateRange.set({ start: startDate, end: endDate });
        this.searchService.selectedPresetDate.set(lastSelectedRange);

        this.filterChanges.forEach((element) => {
          switch (element.filterType) {
            case 'result_type': {
              const fileType = this.searchService.fileTypes().find((ft) => ft.type === element.filter);
              if (fileType) {
                fileType.checked = !fileType.checked;
              }
              break;
            }

            case 'connectors': {
              const connector = this.searchService.connectors().find((cn) => cn.name === element.filter);
              if (connector) {
                connector.checked = !connector.checked;
              }
              break;
            }

            case 'type_code': {
              const typeCode = this.searchService.typeCodes().find((tc) => tc.value === element.filter);
              if (typeCode) {
                typeCode.checked = !typeCode.checked;
              }
              break;
            }

            default:
              break;
          }
        });

        this.searchService.filterResults();
      });
  }

  ngOnDestroy() {
    if (this.saveSubscription != null) this.saveSubscription.unsubscribe();
  }
  /**
   * Listen for clicks outside the search results filters component and close the last searches menu.
   * if menu is open and click is outside the menu and screen width is less than 1195px
   */

  @HostListener('document:click', ['$event']) onClick(event: MouseEvent): void {
    const target = document.querySelector('.search_filters_wrapper');
    if (
      this.searchService.showSearchFilters() === true &&
      target &&
      !target.contains(event.target as Node) &&
      window.innerWidth < 1195
    ) {
      this.searchService.showSearchFilters.set(false);
    }
  }
  /**
   * Returns a boolean indicating whether all connectors are selected or not.
   * @returns {boolean} - True if all connectors are not selected, false otherwise.
   */
  get allConnectorsSelected(): boolean {
    return this.searchService.connectors().filter((cn) => cn.checked).length < 1;
  }

  /**
   * Resets the state of all connectors to unchecked and filters the results.
   */
  public resetConnectors(): void {
    this.searchService.connectors().forEach((cn) => (cn.checked = false));
    this.searchService.filterResults();
  }

  /**
   * Opens the given URL in a new browser tab.
   * @param url - The URL to open.
   */
  public gotoResult(url: string): void {
    window.open(url, '_blank');
  }

  /**
   * Resets the selected preset date and filters the search results based on the selected date range.
   * @returns void
   */
  public onSelectedChange(): void {
    this.searchService.selectedPresetDate.set('');
    this.searchService.filterResults();
  }

  showSelectedFilter(searchFilter: string): boolean {
    return this.filterChanges.findIndex((e: any) => e.filter == searchFilter) >= 0;
  }

  getActiveFilters() {
    if (this.autoSave) return this.searchService.appliedFilters();
    else {
      let currentActiveFilters = this.searchService.appliedFilters();
      this.filterChanges.forEach((element) => {
        const index = currentActiveFilters.findIndex((e) => e.label == element.filter);
        if (index >= 0) {
          currentActiveFilters.splice(index, 1);
        } else {
          currentActiveFilters.push({
            label: element.filter,
            count: 1,
            filter_type: element.filterType,
          });
        }
      });
      return currentActiveFilters;
    }
  }

  /**
   * Toggles the checked state of a FileType object and filters the search results accordingly.
   * @param fileType - The FileType object to toggle.
   */
  public toggleFileType(fileType: FileType): void {
    if (!this.autoSave) {
      const index = this.filterChanges.findIndex((e: any) => e.filter == fileType.type);
      if (index >= 0) this.filterChanges.splice(index, 1);
      else {
        let obj = {
          filterType: 'result_type',
          filter: fileType.type,
        };
        this.filterChanges.push(obj);
      }
    }
    fileType.checked = !fileType.checked;
    this.searchService.filterResults();
  }

  /**
   * Toggles the checked state of the given connector and resets all other connectors.
   * Then, it filters the search results based on the selected connectors.
   * @param connector - The connector to toggle.
   */
  public toggleConnector(connector: ConnectorFilter): void {
    if (!this.autoSave) {
      const index = this.filterChanges.findIndex((e: any) => e.filter == connector.name);
      if (index >= 0) this.filterChanges.splice(index, 1);
      else {
        let obj = {
          filterType: 'connectors',
          filter: connector.name,
        };
        this.filterChanges.push(obj);
      }
    }
    connector.checked = !connector.checked;
    this.searchService.filterResults();
  }

  toggleTypeCode(typeCode: TypeCode): void {
    if (!this.autoSave) {
      const index = this.filterChanges.findIndex((e: any) => e.filter == typeCode.value);
      if (index >= 0) this.filterChanges.splice(index, 1);
      else {
        let obj = {
          filterType: 'type_code',
          filter: typeCode.value,
        };
        this.filterChanges.push(obj);
      }
    }
    typeCode.checked = !typeCode.checked;
    this.searchService.filterResults();
  }

  /**
   * Clear all type code filter and filter the search results.
   */
  clearTypeCodeFilter(): void {
    this.searchService.typeCodes.update((searchTypeCodes: TypeCode[]) =>
      searchTypeCodes.map((tc: TypeCode) => ({ ...tc, checked: false })),
    );
    this.searchService.filterResults();
  }

  /**
   * Clear all file type filter and filter the search results.
   */
  clearFileTypeFilter(): void {
    this.searchService.fileTypes.update((searchFileTypes: FileType[]) =>
      searchFileTypes.map((ft: FileType) => ({ ...ft, checked: false })),
    );
    this.searchService.filterResults();
  }

  /**
   * Clear all connector filter and filter the search results.
   */
  clearConnectorFilter(): void {
    this.searchService.connectors().forEach((cn) => (cn.checked = false));
    this.searchService.filterResults();
  }

  /**
   * Clear all date filter and filter the search results.
   */
  clearDateFilter(): void {
    this.searchService.selectedDateRange.set({ start: '', end: '' });
    this.searchService.selectedPresetDate.set('');
    this.searchService.filterResults();
  }

  /**
   * searchService.selectedPresetDate =
   */
  onPresetDateChange(date: string): void {
    this.searchService.selectedPresetDate.set(date);
    this.searchService.filterResults();
  }

  /**
   * Get checked connectors count
   */
  get checkedConnectorsCount(): number {
    return this.searchService.connectors().filter((cn) => cn.checked).length;
  }

  /**
   * Get checked filetype count
   */
  get checkedFileTypes(): number {
    return this.searchService.fileTypes().filter((ft) => ft.checked).length;
  }

  clearSearchString(): void {
    this.searchService.query.set('');
    this.searchQuery = '';
  }

  onResetFilter(): void {
    if (!this.autoSave) {
      this.searchService.appliedFilters().forEach((filter) => {
        this.resetFilterType(filter.filter_type, filter.label);
      });
    } else {
      this.searchService.resetFilters();
      this.searchService.filterResults();
    }
  }

  /**
   * Reset the filter type
   * @param filterType - The filter type to reset.
   * @param filter - The filter to reset.
   */
  resetFilterType(filterType: string, filter: string): void {
    switch (filterType) {
      case 'result_type': {
        const fileType = this.searchService.fileTypes().find((ft) => ft.type === filter);
        if (fileType) this.toggleFileType(fileType);
        break;
      }

      case 'connectors': {
        const connector = this.searchService.connectors().find((cn) => cn.name === filter);
        if (connector) this.toggleConnector(connector);
        break;
      }

      case 'type_code': {
        const typeCode = this.searchService.typeCodes().find((tc) => tc.label === filter);
        if (typeCode) this.toggleTypeCode(typeCode);
        break;
      }

      case 'date': {
        this.searchService.selectedDateRange.set({ start: '', end: '' });
        this.searchService.selectedPresetDate.set('');
        this.searchService.filterResults();
        break;
      }

      default:
        break;
    }
  }

  toggleShowMoreConnectors(): void {
    this.displayedConnectors =
      this.displayedConnectors === this.maxDisplayed ? this.searchService.connectors().length : this.maxDisplayed;
  }

  toggleShowMoreResultType(): void {
    this.displayedResultType =
      this.displayedResultType === this.maxDisplayed ? this.searchService.fileTypes().length : this.maxDisplayed;
  }

  toggleShowMoreTypeCode(): void {
    this.displayedResultType =
      this.displayedResultType === this.maxDisplayed ? this.searchService.typeCodes().length : this.maxDisplayed;
  }
}
